import CommonButton, { CommonButtonSecondarySx } from "@/components/utils/CommonButton";
import CommonTextField from "@/components/utils/CommonTextField";
import LazoModal from "@/components/utils/LazoModal";
import LazoTooltip from "@/components/utils/LazoTooltip";
import SearchField from "@/components/utils/SearchField";
import { CommonStyledSelect, CommonStyledSelectMenuProps } from "@/components/utils/SelectStyles";
import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import LazoInvitationsService from "@/services/LazoInvitationsService";
import { AddOutlined, ExpandLess, ExpandMore, InfoOutlined } from "@mui/icons-material";
import { Box, FormLabel, MenuItem, Typography, outlinedInputClasses, selectClasses, styled } from "@mui/material";
import { ChangeEvent, Dispatch, RefObject, SetStateAction, useEffect, useState } from "react";
import { TableActions } from "../CustomerTableActions";

import { LazoCheckbox } from "@/components/common/form/LazoCheckbox";
import { LazoView, useLazoView } from "@/contexts/CurrentLazoViewContext";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { LazoCompanyAccountType } from "@/enums/LazoCompanyAccountType";
import UserRole from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/UserRole";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { useResponsive } from "@/hooks/useResponsive";
import LazoRoleService from "@/services/LazoRoleService";
import { LazoInvitationType } from "@/types/LazoInvitationType";
import { mapRolesToString } from "@/types/LazoUserRoles";
import { HttpStatusCode } from "axios";
import { useTranslation } from "react-i18next";

const StyledSelect = styled(CommonStyledSelect)(() => ({
  height: "2.5rem",
  minWidth: "200px",
  borderRadius: "0.7rem",
  color: `${Colors.BLACK}`,
  [`& .${outlinedInputClasses.notchedOutline}`]: {
    border: `1px solid ${Colors.LIGHT_GRAY_BFC2C6}`,
    borderRadius: "0.7rem",
  },
  "&:hover": {
    [`& .${outlinedInputClasses.notchedOutline}`]: {
      border: `1px solid ${Colors.LIGHT_GRAY_BFC2C6}`,
    },
  },
  "&.Mui-focused": {
    border: "none",
    [`& .${outlinedInputClasses.notchedOutline}`]: {
      border: `1px solid ${Colors.LIGHT_GRAY_BFC2C6}`,
      borderRadius: "0.7rem",
    },
  },
  [`& .${selectClasses.select}`]: {
    [`& .${selectClasses.outlined}`]: {
      paddingRight: "1.500rem",
    },
  },
}));

const RowContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "0.5rem",
  alignItems: "center",
  [theme.breakpoints.down("sm")]: {
    flexWrap: "wrap",
  },
}));

const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: "1rem",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    width: "100%",
    gap: "0.75rem",
  },
}));

const ActionsContainer = styled(TableActions)(({ theme }) => ({
  flexDirection: "row",
  padding: 0,
  alignItems: "center",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: "1rem",
    alignItems: "stretch",
  },
}));

const StyledSearchField = styled(SearchField)(({ theme }) => ({
  width: "14rem",
  [theme.breakpoints.down("md")]: {
    width: "12rem",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const FilterButton = styled(CommonButton)(() => ({
  height: "2.5rem",
  borderRadius: "0.5rem",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  backgroundColor: "white",
  color: Colors.BLACK,
  "&:hover": {
    backgroundColor: Colors.ALMOST_WHITE,
  },
}));

const InviteUserButton = styled(CommonButton)(() => ({
  height: "2.5rem",
  borderRadius: "0.5rem",
}));

const FieldContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
});

const StyledFormLabel = styled(FormLabel)({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: Colors.BLACK,
});

type Props = {
  tenantExternalId: string;
  handleSearch: (searchRef: RefObject<HTMLInputElement>) => void;
  setShowFilters: Dispatch<SetStateAction<boolean>>;
  showFilters: boolean;
  handleUpdateInvitationData?: () => void;
  setRefetchData?: Dispatch<SetStateAction<boolean>>;
  refetchData?: boolean;
  onResetFilters?: () => void;
  hideInvite?: boolean;
};

function RoleSelect({ role, setRole, roles }: { role: string; setRole: Dispatch<SetStateAction<string>>; roles: UserRole[] }) {
  return (
    <StyledSelect
      value={role}
      onChange={(e) => setRole(e.target.value as string)}
      IconComponent={ExpandMore}
      MenuProps={CommonStyledSelectMenuProps}
      sx={{
        width: "100%",
      }}
    >
      {roles.map((r) => (
        <MenuItem key={r.externalId} value={r.externalId!}>
          <Typography>{mapRolesToString(r.name)}</Typography>
        </MenuItem>
      ))}
    </StyledSelect>
  );
}

function CustomerDetailsHeaderActions({
  tenantExternalId,
  handleSearch,
  setShowFilters,
  showFilters,
  handleUpdateInvitationData,
  setRefetchData,
  refetchData,
  onResetFilters,
  hideInvite,
}: Props) {
  const { t } = useTranslation(["common", "customer"]);
  const { isMobile } = useResponsive();
  const [isOpenAddUserModal, setIsOpenAddUserModal] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [role, setRole] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [isLazoCompany, setIsLazoCompany] = useState<boolean>(tenantExternalId === "lazoTenant0");
  const [assignPendingTasksOnAcceptance, setAssignPendingTasksOnAcceptance] = useState<boolean>(false);

  /**
   * These are specific to when the tenant is Lazo
   */
  const [allowLazoOne, setAllowLazoOne] = useState<boolean>(false);
  const [lazoOneRole, setLazoOneRole] = useState<string>("");
  const [lazoOneRoles, setLazoOneRoles] = useState<UserRole[]>([]);

  useEffect(() => {
    setIsLazoCompany(tenantExternalId === "lazoTenant0");
  }, [tenantExternalId]);

  useEffect(() => {
    if (isLazoCompany) {
      LazoRoleService.getAllPortalRoles().then((r) => {
        r.onSuccess((content) => {
          setRoles(content);
          if (content[0]) {
            setRole(content[0].externalId!);
          }
        });
        r.onError((e) => console.log("There was an error getting the roles. Message: ", e));
      });
    } else {
      LazoRoleService.getAllCompanyRoles().then((r) => {
        r.onSuccess((content) => {
          setRoles(content);
          if (content[0]) {
            setRole(content[0].externalId!);
          }
        });
        r.onError((e) => console.log("There was an error getting the roles. Message: ", e));
      });
    }
  }, [isLazoCompany]);

  function handleSendInvitation() {
    setSending(true);
    LazoInvitationsService.sendInvitationToCompany({
      content: {
        tenantExternalId: tenantExternalId,
        roleExternalId: role,
        type: LazoInvitationType.USER_TO_COMPANY,
        ...(lazoOneRole && { lazoOneRoleExternalId: lazoOneRole }),
      },
      type: LazoInvitationType.USER_TO_COMPANY,
      email: email.trim(),
      initiatorEmail: AuthService.getUser().email,
      assignPendingTasksOnAcceptance,
    })
      .then((r) => {
        r.onSuccess((_) => {
          NotificationService.info(t("customer:usersTab.messages.invitationSent"));
          setIsOpenAddUserModal(false);
          setAssignPendingTasksOnAcceptance(false);
          handleUpdateInvitationData?.();
          setRefetchData?.(!refetchData);
        });
        r.onError((message, apiResponse) => {
          if (apiResponse.getHttpStatusCode() === HttpStatusCode.BadRequest) {
            NotificationService.warn(t("customer:usersTab.messages.invitationAlreadySent"));
          } else {
            NotificationService.error(message);
          }
        });
      })
      .finally(() => setSending(false));
  }

  function handleAllowAccessToLazoOneCheck(e: ChangeEvent<HTMLInputElement>) {
    setAllowLazoOne(e.currentTarget.checked);
    if (lazoOneRoles.length === 0 && e.currentTarget.checked) {
      LazoRoleService.getAllCompanyRoles().then((r) => {
        r.onSuccess((roles) => {
          setLazoOneRoles(roles);
          setLazoOneRole(roles.find((r) => r.name.includes("viewer")).externalId);
        });
        r.onError((e) => NotificationService.error(t("customer:usersTab.messages.lazoOneRolesError") + e));
      });
    } else if (lazoOneRoles.length > 0 && e.currentTarget.checked) {
      setLazoOneRole(lazoOneRoles.find((r) => r.name.includes("viewer")).externalId);
    } else if (!e.currentTarget.checked) {
      setLazoOneRole("");
    }
  }

  const { lazoView } = useLazoView();
  const { currentCompany, workspaceState } = useTenantMemberships();

  let showInvite = !hideInvite;
  if (showInvite && lazoView === LazoView.LAZO_ONE) {
    showInvite = false;
    if (currentCompany.lazoCompanyAccountType === LazoCompanyAccountType.COMPANY) {
      showInvite = workspaceState.isCompanyAdmin || workspaceState.isLazoAdminOrKam;
    }
  }

  return (
    <>
      <ActionsContainer>
        <StyledSearchField iconVariant handleSearch={handleSearch} displaySearchButton={false} triggerSearchOnInputChange={true} />
        <ButtonsContainer>
          {onResetFilters && (
            <CommonButton sx={CommonButtonSecondarySx} onClick={onResetFilters}>
              <Typography>{t("common:resetAll")}</Typography>
            </CommonButton>
          )}
          <FilterButton endIcon={!showFilters ? <ExpandMore /> : <ExpandLess />} onClick={() => setShowFilters(!showFilters)}>
            <Typography>{t("common:filters")}</Typography>
          </FilterButton>
          {showInvite && (
            <InviteUserButton endIcon={<AddOutlined />} onClick={() => setIsOpenAddUserModal(true)}>
              <Typography>{t("customer:usersTab.headerActions.inviteUser")}</Typography>
            </InviteUserButton>
          )}
        </ButtonsContainer>
      </ActionsContainer>
      <LazoModal
        open={isOpenAddUserModal}
        handleClose={() => setIsOpenAddUserModal(false)}
        title={t("customer:usersTab.headerActions.inviteUserViaEmail", "Invite User via Email")}
        buttons={
          !sending ? (
            <CommonButton
              onClick={() => handleSendInvitation()}
              sx={{
                ...CommonButtonSecondarySx,
                height: "2.750rem",
                borderRadius: "0.500rem",
              }}
            >
              {t("customer:usersTab.headerActions.sendInvite")}
            </CommonButton>
          ) : (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                margin: "0 auto",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Spinner variant="indeterminate" sx={{ color: Colors.LAZO_PURPLE }} />
            </Box>
          )
        }
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <FieldContainer>
            <StyledFormLabel>{t("customer:usersTab.headerActions.emailLabel", "Email")}</StyledFormLabel>
            <CommonTextField
              sx={{
                [`& .${outlinedInputClasses.root}`]: {
                  height: "2.5rem",
                  borderRadius: "0.7rem",
                  [`& .${outlinedInputClasses.notchedOutline}`]: {
                    border: `1px solid ${Colors.LIGHT_GRAY_BFC2C6}`,
                  },
                  "&:hover": {
                    [`& .${outlinedInputClasses.notchedOutline}`]: {
                      border: `1px solid ${Colors.LIGHT_GRAY_BFC2C6}`,
                    },
                  },
                  "&.Mui-focused": {
                    [`& .${outlinedInputClasses.notchedOutline}`]: {
                      border: `1px solid ${Colors.LIGHT_GRAY_BFC2C6}`,
                    },
                  },
                },
                width: "100%",
              }}
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
          </FieldContainer>
          <FieldContainer>
            <StyledFormLabel>{t("customer:usersTab.headerActions.roleLabel", "Role")}</StyledFormLabel>
            <RoleSelect role={role} setRole={setRole} roles={roles} />
          </FieldContainer>
          {isLazoCompany && (
            <>
              <RowContainer>
                <Typography fontSize={"14px"} fontWeight={"400"}>
                  {t("customer:usersTab.headerActions.allowLazoOneAccess")}
                </Typography>
                <LazoCheckbox
                  checked={allowLazoOne}
                  onChange={(e) => {
                    handleAllowAccessToLazoOneCheck(e);
                  }}
                  sx={{
                    width: "1rem",
                    height: "1rem",
                    "&.MuiCheckbox-root": {
                      color: `${Colors.LIGHT_GRAY_DEDEE1}`,
                      "& .MuiSvgIcon-root": {
                        fontSize: "1.15rem",
                      },
                    },
                  }}
                />
              </RowContainer>
              {allowLazoOne && lazoOneRoles.length > 0 && (
                <FieldContainer>
                  <StyledFormLabel>{t("customer:usersTab.headerActions.lazoOneRoleLabel", "Lazo One Role")}</StyledFormLabel>
                  <RoleSelect role={lazoOneRole} setRole={setLazoOneRole} roles={lazoOneRoles} />
                </FieldContainer>
              )}
            </>
          )}
          <RowContainer>
            <LazoCheckbox
              checked={assignPendingTasksOnAcceptance}
              onChange={(e) => setAssignPendingTasksOnAcceptance(e.currentTarget.checked)}
              sx={{
                width: "1rem",
                height: "1rem",
                "&.MuiCheckbox-root": {
                  color: `${Colors.LIGHT_GRAY_DEDEE1}`,
                  "& .MuiSvgIcon-root": {
                    fontSize: "1.15rem",
                  },
                },
              }}
              inputProps={{
                "aria-label": t(
                  "customer:usersTab.headerActions.assignPendingTasksOnAcceptance",
                  "Assign all pending tasks on acceptance"
                ) as string,
              }}
            />
            <Typography fontSize={"14px"} fontWeight={"400"}>
              {t("customer:usersTab.headerActions.assignPendingTasksOnAcceptance", "Assign all pending tasks on acceptance")}
            </Typography>
            <LazoTooltip
              maxWidth="320px"
              title={
                <Typography fontSize={"0.75rem"}>
                  {t(
                    "customer:usersTab.headerActions.assignPendingTasksHint",
                    "When enabled, the new user will automatically be added to every task and workflow currently assigned to the company owner the moment they accept the invitation."
                  )}
                </Typography>
              }
            >
              <InfoOutlined sx={{ fontSize: "1rem", color: Colors.LIGHT_GRAY_8A8E93, cursor: "help" }} />
            </LazoTooltip>
          </RowContainer>
        </Box>
      </LazoModal>
    </>
  );
}

export default CustomerDetailsHeaderActions;
